import { useEffect, useState } from "react";
import { CreditCard, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePermissions } from "@/hooks/usePermissions";
import { PAYER_ROLE_REQUIRED_MESSAGE } from "@/lib/billing-payer";
import { formatPlanConversations } from "@/lib/conversation-count";
import { normalizePayerEmail, payerEmailError as validatePayerEmail } from "@/lib/mercadopago-payer-email";
import { type CardChangeKind, cardChangeView, defaultCardChangeKind, type PlanChangeOptions } from "@/lib/plan-change";
import { CardPlanChange, cardChangeConfirmLabel, cardChangeTitle } from "@/components/settings/plan/CardPlanChange";

export interface PlanChangeDialogPlan {
  id?: string;
  name: string;
  price_monthly: number;
  conversations_included: number;
  billing_mode?: string | null;
  payment_strategy?: string | null;
}

export interface PlanChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: PlanChangeDialogPlan | null | undefined;
  currentIncluded?: number | null;
  selectedPlan: PlanChangeDialogPlan | null;
  isPlanChange: boolean;
  hasPreapproval: boolean;
  loading: boolean;
  defaultPayerEmail: string;
  onConfirm: (payerEmail: string | undefined) => void;
  canPay?: boolean;
  cardChange?: PlanChangeOptions | null;
  cardChangeLoading?: boolean;
  onConfirmCardChange?: (kind: CardChangeKind) => void;
}

export function planChangeStrategy(plan: Pick<PlanChangeDialogPlan, "payment_strategy" | "price_monthly">): string {
  return plan.payment_strategy || (plan.price_monthly > 0 ? "preapproval" : "cash");
}

export function PlanChangeDialog({
  open,
  onOpenChange,
  currentPlan,
  currentIncluded,
  selectedPlan,
  isPlanChange,
  hasPreapproval,
  loading,
  defaultPayerEmail,
  onConfirm,
  canPay = true,
  cardChange,
  cardChangeLoading = false,
  onConfirmCardChange,
}: PlanChangeDialogProps) {
  const { can, isLoading: permsLoading, isError: permsError } = usePermissions();
  const canChoosePayer = !permsLoading && !permsError && can("settings.account");
  const [payerEmail, setPayerEmail] = useState(defaultPayerEmail);
  const [payerEmailError, setPayerEmailError] = useState<string | null>(null);
  const view = onConfirmCardChange ? cardChangeView(hasPreapproval, cardChange, cardChangeLoading) : "signup";
  const cardFlow = view !== "signup";
  const [kind, setKind] = useState<CardChangeKind>(defaultCardChangeKind(view));

  useEffect(() => {
    if (!open) return;
    setPayerEmail(defaultPayerEmail);
    setPayerEmailError(null);
  }, [open, defaultPayerEmail]);

  useEffect(() => {
    setKind(defaultCardChangeKind(view));
  }, [view, selectedPlan?.id]);

  const asksPayerEmail =
    !cardFlow && canChoosePayer && !!selectedPlan && !hasPreapproval && planChangeStrategy(selectedPlan) === "preapproval";
  const canConfirm = !cardFlow || view === "upgrade" || view === "next_cycle";

  const handleConfirm = () => {
    if (!canPay) return;
    if (cardFlow) {
      if (canConfirm) onConfirmCardChange?.(kind);
      return;
    }
    if (!asksPayerEmail) {
      onConfirm(undefined);
      return;
    }
    const error = validatePayerEmail(payerEmail);
    if (error) {
      setPayerEmailError(error);
      return;
    }
    setPayerEmailError(null);
    onConfirm(normalizePayerEmail(payerEmail));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" aria-hidden="true" />
            {cardFlow
              ? cardChangeTitle(view, selectedPlan?.name)
              : isPlanChange ? `Cambiar a ${selectedPlan?.name}` : `Suscribirse al plan ${selectedPlan?.name}`}
          </DialogTitle>
          <DialogDescription>
            {(() => {
              if (view === "upgrade") return "Pagas hoy la diferencia y el cambio es inmediato. Tu débito automático se ajusta solo.";
              if (view === "next_cycle") return "Programamos el cambio para tu próximo ciclo. Hoy no pagas nada.";
              if (cardFlow) return "Cambiar de plan con tu débito automático activo.";
              const s = selectedPlan?.payment_strategy || "preapproval";
              if (s === "cash") return "Este plan se cobra fuera de MercadoPago. Te conectaremos con un ejecutivo comercial.";
              if (s === "manual_oneshot") return "Te llevaremos a MercadoPago Checkout para completar el pago de este ciclo.";
              if (hasPreapproval) return "Cancela tu suscripción actual primero. MercadoPago no permite dos suscripciones activas a la vez.";
              return "Te llevaremos a MercadoPago para autorizar el cobro automático mensual. Solo tienes que aprobarlo una vez.";
            })()}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {selectedPlan && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-muted/50 p-3 text-center">
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Plan actual</p>
                <p className="text-sm font-bold">{currentPlan?.name || "Explora"}</p>
                <p className="text-xs text-muted-foreground">{formatPlanConversations({ billing_mode: currentPlan?.billing_mode, conversations_included: currentIncluded ?? currentPlan?.conversations_included ?? 0 }, "conv/mes")}</p>
                <p className="text-sm font-semibold mt-1">${(currentPlan?.price_monthly || 0).toLocaleString("es-CO")}</p>
              </div>
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-center ring-1 ring-primary/20">
                <p className="text-[10px] font-medium uppercase tracking-wider text-primary mb-1">Nuevo plan</p>
                <p className="text-sm font-bold">{selectedPlan.name}</p>
                <p className="text-xs text-muted-foreground">{formatPlanConversations(selectedPlan, "conv/mes")}</p>
                <p className="text-sm font-semibold mt-1">${selectedPlan.price_monthly.toLocaleString("es-CO")}</p>
              </div>
            </div>
          )}
          {cardFlow && (
            <CardPlanChange view={view} options={cardChange} kind={kind} onKindChange={setKind} />
          )}
          {asksPayerEmail && (
            <div className="space-y-2">
              <Label htmlFor="plan-change-payer-email" className="text-sm">Correo de la cuenta de MercadoPago que va a pagar</Label>
              <Input
                id="plan-change-payer-email"
                type="email"
                autoComplete="email"
                value={payerEmail}
                onChange={(e) => {
                  setPayerEmail(e.target.value);
                  if (payerEmailError) setPayerEmailError(null);
                }}
                aria-invalid={payerEmailError ? true : undefined}
                aria-describedby={payerEmailError ? "plan-change-payer-email-help plan-change-payer-email-error" : "plan-change-payer-email-help"}
                disabled={loading}
              />
              {payerEmailError && (
                <p id="plan-change-payer-email-error" role="alert" className="text-xs font-medium text-destructive">
                  {payerEmailError}
                </p>
              )}
              <div className="flex gap-2 items-start rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40 p-2.5">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" aria-hidden="true" />
                <p id="plan-change-payer-email-help" className="text-xs text-blue-800 dark:text-blue-300">
                  Debe ser el correo de la cuenta de MercadoPago con la que se hará el pago. Si paga otra persona
                  o el área de pagos de tu empresa, escribe el correo de esa cuenta.
                </p>
              </div>
            </div>
          )}
          {!canPay && canConfirm && (
            <p role="note" data-testid="plan-change-payer-required" className="text-xs text-muted-foreground">
              {PAYER_ROLE_REQUIRED_MESSAGE}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancelar</Button>
          {canConfirm && (
            <Button
              onClick={handleConfirm}
              className="gap-1.5"
              disabled={loading || !canPay}
              title={canPay ? undefined : PAYER_ROLE_REQUIRED_MESSAGE}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CreditCard className="h-4 w-4" aria-hidden="true" />}
              {cardFlow
                ? loading ? "Procesando..." : cardChangeConfirmLabel(view, kind, cardChange)
                : loading ? "Redirigiendo..." : isPlanChange ? "Cambiar plan" : "Suscribirse"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
